/**
 * View Session Template - Platform/Club admin view for session template details
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, AdminPageLayout, Card, Section, Grid, Col, Badge } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';
import { Clock, ListOrdered } from 'lucide-react';

const ViewSessionTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: templateId } = useParams();
  const { showSuccess, showError } = useToast();

  const [template, setTemplate] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine context from URL (memoized to prevent recreating on every render)
  const currentPath = location.pathname;
  const { isPlatformAdmin, isClubAdmin, isBranchAdmin } = useMemo(() => ({
    isPlatformAdmin: currentPath.startsWith('/admin/system-templates'),
    isClubAdmin: currentPath.startsWith('/admin/templates'),
    isBranchAdmin: !!currentPath.match(/^\/branch\/[^/]+\/templates/)
  }), [currentPath]);
  const isCoach = currentPath.startsWith('/coach/templates');

  // Set back path
  let backPath;
  if (isPlatformAdmin) {
    backPath = '/admin/system-templates/sessions';
  } else if (isClubAdmin) {
    backPath = '/admin/templates/sessions';
  } else if (isBranchAdmin) {
    const branchId = currentPath.split('/')[2];
    backPath = `/branch/${branchId}/templates/sessions`;
  } else if (isCoach) {
    backPath = '/coach/templates/sessions';
  } else {
    backPath = '/admin/system-templates/sessions';
  }

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let templateData;
      if (isPlatformAdmin) {
        templateData = await apiService.get(`/api/admin/session-templates/${templateId}?include_activities=true`);
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        templateData = await apiService.get(`/api/clubs/${clubId}/session-templates/${templateId}?include_activities=true`);
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        templateData = await apiService.get(`/api/branches/${branchId}/session-templates/${templateId}?include_activities=true`);
      } else if (isCoach) {
        // Coaches load from branch endpoint using their location_id
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        templateData = await apiService.get(`/api/branches/${branchId}/session-templates/${templateId}?include_activities=true`);
      }

      setTemplate(templateData);
      setActivities(templateData?.activities || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, isPlatformAdmin, isClubAdmin, isBranchAdmin]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleActivate = async () => {
    try {
      if (isPlatformAdmin) {
        await apiService.put(`/api/admin/session-templates/${templateId}`, { is_active: true });
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        await apiService.put(`/api/clubs/${clubId}/session-templates/${templateId}`, { is_active: true });
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        await apiService.put(`/api/branches/${branchId}/session-templates/${templateId}`, { is_active: true });
      } else if (isCoach) {
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        await apiService.put(`/api/branches/${branchId}/session-templates/${templateId}`, { is_active: true });
      }
      showSuccess('Session template activated successfully');
      loadTemplate();
    } catch (err) {
      showError(`Failed to activate: ${err.message}`);
    }
  };

  const handleDeactivate = async () => {
    try {
      if (isPlatformAdmin) {
        await apiService.put(`/api/admin/session-templates/${templateId}`, { is_active: false });
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        await apiService.put(`/api/clubs/${clubId}/session-templates/${templateId}`, { is_active: false });
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        await apiService.put(`/api/branches/${branchId}/session-templates/${templateId}`, { is_active: false });
      } else if (isCoach) {
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        await apiService.put(`/api/branches/${branchId}/session-templates/${templateId}`, { is_active: false });
      }
      showSuccess('Session template deactivated successfully');
      loadTemplate();
    } catch (err) {
      showError(`Failed to deactivate: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="VIEW SESSION" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Loading session details...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout title="VIEW SESSION" maxWidth="1400px">
        <Section title="Error">
          <Card level={1}>
            <p style={{ color: 'var(--color-error)', marginBottom: '1.5rem' }}>Failed to load session: {error}</p>
            <Button onClick={loadTemplate} variant="primary">Try Again</Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  if (!template) {
    return (
      <AdminPageLayout title="VIEW SESSION" maxWidth="1400px">
        <Section title="Not Found">
          <Card level={1}>
            <p style={{ opacity: 0.6, marginBottom: '1.5rem' }}>This session could not be found.</p>
            <Button onClick={() => navigate(backPath)} variant="outline">
              Back to Sessions
            </Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  const editPath = isPlatformAdmin 
    ? `/admin/system-templates/sessions/${templateId}/edit`
    : isClubAdmin
    ? `/admin/templates/sessions/${templateId}/edit`
    : isBranchAdmin
    ? `/branch/${currentPath.split('/')[2]}/templates/sessions/${templateId}/edit`
    : isCoach
    ? `/coach/templates/sessions/${templateId}/edit`
    : `/admin/system-templates/sessions/${templateId}/edit`;

  const totalDuration = activities.reduce((sum, act) => sum + (act.duration_mins || 0), 0);

  return (
    <AdminPageLayout
      title="VIEW SESSION"
      subtitle={template.name}
      backPath={backPath}
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

        <Section
          title="Status"
          actions={
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button
                onClick={() => navigate(editPath)}
                variant="edit"
              >
                Edit Session
              </Button>
              {template.is_active ? (
                <Button
                  onClick={handleDeactivate}
                  variant="warning"
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  onClick={handleActivate}
                  variant="success"
                >
                  Activate
                </Button>
              )}
            </div>
          }
        >
          <Card level={1}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'var(--surface-lvl2)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--color-border-light)'
              }}>
                <Badge variant={template.is_active ? 'success' : 'warning'} dot size="lg" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>{template.name}</div>
                <div style={{ opacity: 0.6, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Clock size={16} />
                    {template.total_duration_mins || 0} minutes
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ListOrdered size={16} />
                    {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
                  </span>
                </div>
              </div>
              <Badge variant={template.is_active ? 'success' : 'warning'} size="sm">
                {template.is_active ? 'ACTIVE' : 'INACTIVE'}
              </Badge>
            </div>
          </Card>
        </Section>

        <Grid columns={12} gap="2rem">
          <Col span={8}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Section title="Details">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Description</div>
                      <p style={{ lineHeight: 1.6, fontSize: '1rem' }}>{template.description || 'No description provided.'}</p>
                    </div>
                  </div>
                </Card>
              </Section>

              <Section 
                title="Activities" 
                subtitle={activities.length > 0 ? `${activities.length} activities • ${totalDuration} min total` : 'No activities added'}
              >
                {activities.length === 0 ? (
                  <Card level={1}>
                    <div style={{ padding: '2rem', textAlign: 'center', opacity: 0.5 }}>
                      No activities have been added to this session yet.
                    </div>
                  </Card>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {activities
                      .sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0))
                      .map((activity, index) => (
                        <Card key={index} level={1}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              background: 'var(--surface-lvl2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              border: '1px solid var(--color-border-light)'
                            }}>
                              {index + 1}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                                {activity.activity_name || 'Unknown Activity'}
                              </div>
                              {activity.notes && (
                                <div style={{ fontSize: '0.85rem', opacity: 0.6, fontStyle: 'italic' }}>
                                  {activity.notes}
                                </div>
                              )}
                            </div>
                            <Badge variant="neutral" size="sm">
                              <Clock size={14} style={{ marginRight: '0.25rem' }} />
                              {activity.duration_mins || 0} min
                            </Badge>
                          </div>
                        </Card>
                      ))}
                  </div>
                )}
              </Section>
            </div>
          </Col>

          <Col span={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Section title="Information">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Duration</span>
                      <span style={{ fontWeight: 600 }}>{template.total_duration_mins || 0} min</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Activities</span>
                      <span style={{ fontWeight: 600 }}>{activities.length}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Scope</span>
                      <span style={{ fontWeight: 600 }}>
                        <Badge variant="info" size="sm">{template.scope || 'platform'}</Badge>
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Created</span>
                      <span style={{ fontWeight: 600 }}>{template.created_at ? new Date(template.created_at).toLocaleDateString() : 'Unknown'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Last Updated</span>
                      <span style={{ fontWeight: 600 }}>{template.updated_at ? new Date(template.updated_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </Card>
              </Section>

              {totalDuration !== template.total_duration_mins && (
                <Section title="Duration Warning">
                  <Card level={1} style={{ border: '1px solid var(--color-warning)' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', padding: '0.5rem' }}>
                      <div style={{ color: 'var(--color-warning)' }}>⚠️</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>Duration Mismatch</div>
                        <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                          Activities total ({totalDuration} min) doesn't match session duration ({template.total_duration_mins} min)
                        </div>
                      </div>
                    </div>
                  </Card>
                </Section>
              )}
            </div>
          </Col>
        </Grid>
      </div>
    </AdminPageLayout>
  );
};

export default ViewSessionTemplate;
